/**
 * Typed API client.
 *
 *   • Single base path: /api  (proxied to backend in dev; absolute in prod via VITE_API_URL).
 *   • Adds Bearer access token if present.
 *   • On 401: triggers exactly one refresh flow, then retries the original request.
 *   • All errors land as ApiError instances (caller can check .status / .code).
 */
import type { ApiOk, ApiPaged, ApiErr, AuthResult } from "@/types/api";
import { apiUrl } from "./env";
import { markReachable } from "@/offline/connectivity";
import { saveBlob, downloadRemoteFileNative } from "./attachment-actions";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { clientHeaders } from "./client-info";

/* ─────────────── Token store (in-memory access; refresh in cookie) ────
 *
 * Storage model after the cookie migration:
 *
 *   - Access token: JS module variable below. Never written to
 *     localStorage / sessionStorage / cookies. Lifespan = current
 *     page-load. On page reload it's reconstructed by hitting
 *     /api/auth/refresh, which reads the refresh cookie.
 *
 *   - Refresh token: httpOnly + SameSite=Strict cookie issued by the
 *     backend (icheck_rt at /api/auth). JavaScript cannot read it —
 *     intentionally, so an XSS attacker can't exfiltrate it. The browser
 *     auto-attaches it to /api/auth requests when credentials:"include"
 *     is set (it is — see rawRequest below).
 *
 * Older builds wrote both tokens to localStorage; we sweep those keys
 * here so DevTools' Application panel is clean after this migration.
 */

const LEGACY_KEYS_TO_PURGE = [
  "icheck.accessToken",   // was: in-memory access
  "icheck.refreshToken",  // was: refresh duplicated alongside the cookie
  "loginFlag",            // legacy marker from an even older build
];

// Native (Capacitor) can't persist the cross-origin refresh cookie across app
// restarts, so we keep the refresh token + the fixed session deadline in
// Preferences (durable). Web keeps the refresh token in the httpOnly cookie and
// never stores it in JS (XSS-safe), so these are native-only.
const IS_NATIVE = Capacitor.isNativePlatform();
const RT_KEY = "icheck.rt";
const RT_EXP_KEY = "icheck.rtExp";

let accessToken: string | null = null;
let refreshToken: string | null = null;        // native only
let sessionExpiresAt: string | null = null;     // native only (ISO; fixed login+TTL)

// A stable per-install device id, persisted across restarts and logouts (it
// identifies the DEVICE, not the session). Sent at login so the backend keeps
// each device's session independent and scopes refresh-token reuse revocation
// to the offending device only — logging in (or a token race) on one device no
// longer logs the user out everywhere.
const DEVICE_ID_KEY = "icheck.deviceId";
let deviceIdCache: string | null = null;

function genDeviceId(): string {
  try { if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID(); } catch { /* */ }
  return `dev-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

async function getDeviceId(): Promise<string> {
  if (deviceIdCache) return deviceIdCache;
  if (IS_NATIVE) {
    const stored = (await Preferences.get({ key: DEVICE_ID_KEY })).value;
    deviceIdCache = stored ?? genDeviceId();
    if (!stored) await Preferences.set({ key: DEVICE_ID_KEY, value: deviceIdCache });
    return deviceIdCache;
  }
  try {
    const stored = localStorage.getItem(DEVICE_ID_KEY);
    deviceIdCache = stored ?? genDeviceId();
    if (!stored) localStorage.setItem(DEVICE_ID_KEY, deviceIdCache);
    return deviceIdCache;
  } catch {
    // Storage disabled (private mode) — an ephemeral per-load id still isolates
    // this tab's session from other devices for the duration of the session.
    deviceIdCache = genDeviceId();
    return deviceIdCache;
  }
}

try {
  for (const k of LEGACY_KEYS_TO_PURGE) localStorage.removeItem(k);
} catch { /* private mode / disabled storage — harmless */ }

export const tokens = {
  get access(): string | null { return accessToken; },
  /** The persisted refresh token (native) — null on web (it's in the cookie). */
  get refresh(): string | null { return refreshToken; },
  /** Fixed session deadline (ISO) — when the 1-month window ends. Native only. */
  get sessionExpiresAt(): string | null { return sessionExpiresAt; },

  /** Store the token set after login/refresh. On native the refresh token +
   *  session deadline are persisted to Preferences so the session survives app
   *  restarts; on web the refresh token stays in the httpOnly cookie and the
   *  body value is ignored (kept out of JS to stay XSS-safe). */
  async set(access: string | null, refresh?: string | null, sessionExp?: string | null) {
    accessToken = access;
    if (!IS_NATIVE) return;
    if (refresh !== undefined) {
      refreshToken = refresh;
      if (refresh) await Preferences.set({ key: RT_KEY, value: refresh });
      else await Preferences.remove({ key: RT_KEY });
    }
    if (sessionExp !== undefined) {
      sessionExpiresAt = sessionExp;
      if (sessionExp) await Preferences.set({ key: RT_EXP_KEY, value: sessionExp });
      else await Preferences.remove({ key: RT_EXP_KEY });
    }
  },

  /** Load the persisted refresh token on cold start (native). No-op on web. */
  async load() {
    if (!IS_NATIVE) return;
    refreshToken = (await Preferences.get({ key: RT_KEY })).value;
    sessionExpiresAt = (await Preferences.get({ key: RT_EXP_KEY })).value;
  },

  /** Wipe all client-side tokens. The httpOnly cookie (web) is cleared by the
   *  backend logout; native Preferences are cleared here. */
  async clear() {
    accessToken = null;
    refreshToken = null;
    sessionExpiresAt = null;
    clearConditionalCache(); // never let a next user 304-read a prior user's body
    if (IS_NATIVE) {
      await Preferences.remove({ key: RT_KEY });
      await Preferences.remove({ key: RT_EXP_KEY });
    }
    try {
      for (const k of LEGACY_KEYS_TO_PURGE) localStorage.removeItem(k);
    } catch { /* harmless */ }
  },
};

/* ─────────────── Error type ─────────────── */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/* ─────────────── Refresh-flow guard ─────────────── */

/** "ok" = refreshed; "expired" = session can't be refreshed (over → log out);
 *  "transient" = network/server hiccup (offline) → stay signed in, retry later. */
type RefreshResult = "ok" | "expired" | "transient";

let inflightRefresh: Promise<RefreshResult> | null = null;

async function refreshTokens(): Promise<RefreshResult> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async (): Promise<RefreshResult> => {
    try {
      // Native sends the persisted refresh token in the body (the cross-origin
      // cookie doesn't survive in the WKWebView); web relies on the httpOnly
      // cookie (credentials:"include"). The backend rotates the token and
      // returns the new one + the fixed session deadline.
      const useBody = IS_NATIVE && !!refreshToken;
      const res = await fetch(apiUrl("/api/auth/refresh"), {
        method: "POST",
        credentials: "include",
        headers: useBody ? { "Content-Type": "application/json" } : undefined,
        body: useBody ? JSON.stringify({ refreshToken }) : undefined,
      });
      if (res.ok) {
        const json = (await res.json()) as ApiOk<{
          accessToken: string; refreshToken?: string; sessionExpiresAt?: string;
        }>;
        if (!json.success) return "transient";
        await tokens.set(
          json.data.accessToken,
          json.data.refreshToken ?? undefined,
          json.data.sessionExpiresAt ?? undefined,
        );
        return "ok";
      }
      // 401 from /refresh = token expired / revoked / reused / missing → the
      // session is over and cannot be refreshed.
      if (res.status === 401) return "expired";
      // 403 AWAITING_VESSEL_ASSIGNMENT = a seafarer signed off their vessel
      // since signing in; the session is over too.
      if (res.status === 403) {
        const code = ((await res.json().catch(() => null)) as ApiErr | null)?.error?.code;
        if (code === "AWAITING_VESSEL_ASSIGNMENT") return "expired";
      }
      // 5xx / proxy errors → transient; don't end the session.
      return "transient";
    } catch {
      // Network error (offline / unreachable) → transient; keep the session.
      return "transient";
    } finally {
      setTimeout(() => { inflightRefresh = null; }, 0);
    }
  })();
  return inflightRefresh;
}

/* ─────────────── Logout broadcast (set by AuthContext) ─────────────── */

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

/* ── App-identity headers + upgrade-required (set by the app-version layer) ──
 * Registered rather than imported to avoid a cycle (app-version imports `api`).
 * The provider stamps X-App-Platform/Store/Build/Version on every request so the
 * server can enforce a minimum build; the 426 handler flips the app to the
 * force-update screen. Both are no-ops on web (nothing registers them). */
let appHeadersProvider: (() => Record<string, string>) | null = null;
export function setAppHeadersProvider(fn: (() => Record<string, string>) | null) {
  appHeadersProvider = fn;
}
let onUpgradeRequired: (() => void) | null = null;
export function setUpgradeRequiredHandler(fn: (() => void) | null) {
  onUpgradeRequired = fn;
}
// Fired when the backend rejects a request because the caller's COMPANY has been
// disabled (403 COMPANY_DISABLED) — even on a still-valid session. AuthContext
// registers this to show the message and end the session.
let onCompanyDisabled: ((message: string) => void) | null = null;
export function setCompanyDisabledHandler(fn: ((message: string) => void) | null) {
  onCompanyDisabled = fn;
}

/* ─────────────── Core request ─────────────── */

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;                 // JSON serialized; for FormData pass `formData` instead
  formData?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip auth attachment (e.g. login). */
  anonymous?: boolean;
  /** Skip auto-refresh on 401 (used by the refresh call itself). */
  noRefresh?: boolean;
  /** Conditional GET: send If-None-Match from a per-URL ETag cache so an
   *  unchanged response comes back as a bodiless 304 and we return the cached
   *  body without the server rebuilding it. Opt-in — used by the polled
   *  live-view queries. See {@link conditionalCache}. */
  etag?: boolean;
}

/* Conditional-GET (ETag) cache — opt-in per request via `etag: true`. Lets a
 * polled endpoint send If-None-Match so the server can answer an unchanged poll
 * with a 304 and skip its recompute. Keyed by final URL, capped so it can't
 * grow unbounded, and cleared on logout (see tokens.clear). */
const CONDITIONAL_MAX = 64;
const conditionalCache = new Map<string, { etag: string; body: unknown }>();
/** Drop all cached ETag bodies — called on logout so a next user never reads a
 *  prior user's cached response off a 304. */
export function clearConditionalCache(): void { conditionalCache.clear(); }

function buildQuery(q: RequestOptions["query"]): string {
  if (!q) return "";
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null) continue;
    usp.append(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

function genReqId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
}

async function rawRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = apiUrl(path) + buildQuery(opts.query);
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Request-Id": genReqId(),
    ...(appHeadersProvider?.() ?? {}),   // X-App-Platform/Store/Build/Version (native, force-update)
    ...clientHeaders(),                  // X-Client-Version / -Platform / -Build / -Commit
    ...(opts.headers ?? {}),
  };

  let body: BodyInit | undefined;
  if (opts.formData) {
    body = opts.formData;                                       // browser sets multipart boundary
  } else if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }

  if (!opts.anonymous) {
    const t = tokens.access;
    if (t) headers.Authorization = `Bearer ${t}`;
  }

  // Conditional GET: revalidate against our cached ETag. `cache: "no-store"`
  // keeps the browser's own HTTP cache out of it, so the 304 decision is the
  // server's alone (deterministic across web + native webview).
  const conditional = opts.etag === true;
  if (conditional) {
    const cached = conditionalCache.get(url);
    if (cached) headers["If-None-Match"] = cached.etag;
  }

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? "GET",
      headers,
      body,
      credentials: "include",
      signal: opts.signal,
      ...(conditional ? { cache: "no-store" as RequestCache } : {}),
    });
    // A 502/503/504 means the dev/preview proxy reached US but the BACKEND is
    // down — treat that as unreachable (otherwise we'd flip back "online" on a
    // gateway error and keep firing doomed requests). Any other status means
    // the backend actually handled the request → reachable.
    markReachable(![502, 503, 504].includes(res.status));
  } catch (networkErr) {
    // A network-level failure (offline / server unreachable) — record it so the
    // offline layer routes subsequent reads straight to the cache instead of
    // firing more doomed requests. Aborts are not connectivity signals.
    if (!(networkErr instanceof DOMException && networkErr.name === "AbortError")) {
      markReachable(false);
    }
    throw networkErr;
  }

  // 401: try refresh once, then retry the original request.
  if (res.status === 401 && !opts.anonymous && !opts.noRefresh) {
    const result = await refreshTokens();
    if (result === "ok") return rawRequest<T>(path, { ...opts, noRefresh: true });
    // Only end the session when it genuinely can't be refreshed (1-month
    // deadline / revoked). The handler (AuthContext) flushes the offline outbox
    // first, then logs out. A transient failure (offline / server) leaves the
    // session intact — offline reads come from cache and the next online
    // request retries the refresh.
    if (result === "expired") onUnauthorized?.();
  }

  // 426: this build is below the enforced minimum for its store. Surface the
  // blocking force-update screen; the request still throws below so the caller
  // fails cleanly (the force screen takes over the UI regardless).
  if (res.status === 426) onUpgradeRequired?.();

  // 304 Not Modified (conditional GET): the server's version matched our
  // If-None-Match — nothing changed, so return the cached body without parsing.
  if (conditional && res.status === 304) {
    const cached = conditionalCache.get(url);
    if (cached) return cached.body as T;
    // Cache was cleared between sending If-None-Match and the 304 — refetch
    // unconditionally so we still return real data.
    return rawRequest<T>(path, { ...opts, etag: false });
  }

  // Handle non-JSON / empty bodies gracefully
  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  const json = isJson ? await res.json() : null;

  if (!res.ok) {
    const err = json as ApiErr | null;
    // Company disabled: a still-valid session whose company was turned off by
    // the platform admin. Trigger the global handler (message + end session)
    // instead of letting callers surface repeated 403s. Mirrors 401/426.
    if (res.status === 403 && err?.error?.code === "COMPANY_DISABLED") {
      onCompanyDisabled?.(err.error.message ?? "Your company account has been disabled.");
    }
    throw new ApiError(
      res.status,
      err?.error?.code ?? "HTTP_ERROR",
      err?.error?.message ?? `HTTP ${res.status}`,
      err?.error?.details,
    );
  }

  // Cache the fresh body against its ETag so the next poll can revalidate to a
  // 304. Bounded FIFO eviction keeps the map small.
  if (conditional) {
    const et = res.headers.get("etag");
    if (et) {
      if (conditionalCache.size >= CONDITIONAL_MAX && !conditionalCache.has(url)) {
        const oldest = conditionalCache.keys().next().value;
        if (oldest !== undefined) conditionalCache.delete(oldest);
      }
      conditionalCache.set(url, { etag: et, body: json });
    }
  }

  return json as T;
}

/* ─────────────── Convenience wrappers ─────────────── */

export const api = {
  get:    <T>(path: string, opts?: RequestOptions)               => rawRequest<T>(path, { ...opts, method: "GET"    }),
  post:   <T>(path: string, body?: unknown, opts?: RequestOptions) => rawRequest<T>(path, { ...opts, method: "POST",   body }),
  put:    <T>(path: string, body?: unknown, opts?: RequestOptions) => rawRequest<T>(path, { ...opts, method: "PUT",    body }),
  patch:  <T>(path: string, body?: unknown, opts?: RequestOptions) => rawRequest<T>(path, { ...opts, method: "PATCH",  body }),
  delete: <T>(path: string, opts?: RequestOptions)               => rawRequest<T>(path, { ...opts, method: "DELETE" }),

  /** Helper for typical paged endpoints. */
  paged: async <T>(path: string, query?: RequestOptions["query"]): Promise<ApiPaged<T>> =>
    rawRequest<ApiPaged<T>>(path, { method: "GET", query }),

  /** Helper for typical single-data envelope endpoints. */
  one: async <T>(path: string, opts?: RequestOptions): Promise<T> => {
    const json = await rawRequest<ApiOk<T>>(path, opts);
    return json.data;
  },
};

/* ─────────────── Auth-specific helpers ─────────────── */

export const authApi = {
  login: async (
    /** Email (Master + shore-side users) OR a rank-IMO login code
     *  (vessel crew, e.g. "choff.9739989"). Parameter name kept as
     *  `email` for backward compatibility with existing call sites; the
     *  backend accepts the value under either `loginId` or `email`. */
    email: string,
    password: string,
    deviceId?: string,
    mfaToken?: string,
  ): Promise<AuthResult | { mfaRequired: true }> => {
    const json = await rawRequest<ApiOk<AuthResult | { mfaRequired: true }>>("/api/auth/login", {
      method: "POST",
      // platform selects the session length server-side (native 30d / web 7d).
      body: { loginId: email, password, deviceId: deviceId ?? await getDeviceId(), mfaToken, platform: IS_NATIVE ? "native" : "web" },
      anonymous: true,
    });
    // A non-JSON 200 (e.g. an HTML page from a misrouted request or a stale
    // service worker serving index.html) leaves `json` null — guard it with a
    // clear message instead of the cryptic "d.data is null".
    if (!json || typeof json !== "object" || !("data" in json)) {
      throw new ApiError(0, "BAD_RESPONSE", "Couldn't reach the API: the server didn't return JSON. Check that the app's API URL points at the running backend (and reinstall the app to clear any old service worker).");
    }
    // MFA-enabled accounts return a challenge on the first step (no tokens).
    if ("mfaRequired" in json.data && json.data.mfaRequired) {
      return { mfaRequired: true };
    }
    const result = json.data as AuthResult;
    // Web: refresh token is in the httpOnly cookie; we only stash the access
    // token. Native: persist the body refresh token + session deadline (set()
    // ignores them on web). credentials:"include" carries the cookie either way.
    await tokens.set(
      result.accessToken,
      result.refreshToken ?? undefined,
      result.sessionExpiresAt ?? undefined,
    );
    return result;
  },

  logout: async () => {
    try {
      // Native sends the refresh token so the backend can revoke the right row
      // (no cookie); web relies on the cookie. credentials:"include" is set by
      // rawRequest.
      await rawRequest<ApiOk<{ ok: boolean }>>("/api/auth/logout", {
        method: "POST",
        body: IS_NATIVE && tokens.refresh ? { refreshToken: tokens.refresh } : undefined,
      });
    } finally {
      await tokens.clear();
    }
  },

  me: async () => api.one<import("@/types/api").User>("/api/auth/me"),

  /** Mint a fresh access token from the persisted refresh token (native) or
   *  the refresh cookie (web). Used by the AuthContext bootstrap on every load
   *  (access lives only in JS memory). Returns true only on a clean refresh;
   *  an expired/rejected session and transient failures both return false (the
   *  bootstrap just shows the login screen — no sync-logout from here). */
  refreshAccessFromCookie: async (): Promise<boolean> => (await refreshTokens()) === "ok",

  changePassword: async (currentPassword: string, newPassword: string) =>
    // Backend route is PATCH /api/auth/password — POST 404s (the bug behind
    // "change password not working").
    api.patch<ApiOk<{ ok: boolean }>>("/api/auth/password", { currentPassword, newPassword }),

  /** Set the signed-in user's profile photo. `avatar` is a small cropped data
   *  URL (data:image/jpeg;base64,…) produced by the avatar editor. */
  setAvatar: async (avatar: string) =>
    api.put<ApiOk<{ avatarPath: string | null }>>("/api/auth/me/avatar", { avatar }),

  /** Remove the profile photo (revert to initials). */
  removeAvatar: async () =>
    api.delete<ApiOk<{ avatarPath: string | null }>>("/api/auth/me/avatar"),

  /** Request a password-reset link. Always resolves (the backend never reveals
   *  whether the email has an account). Public endpoint. */
  forgotPassword: async (email: string) =>
    api.post<ApiOk<{ ok: boolean }>>("/api/auth/forgot-password", { email }, { anonymous: true }),

  /** Validate a reset token (for the reset page). Throws if invalid/expired. */
  previewReset: async (token: string) =>
    api.get<ApiOk<{ email: string }>>(`/api/auth/reset-password/${encodeURIComponent(token)}`, { anonymous: true }).then((r) => r.data),

  /** Consume a reset token and set a new password. Public endpoint. */
  resetPassword: async (token: string, password: string) =>
    api.post<ApiOk<{ ok: boolean }>>("/api/auth/reset-password", { token, password }, { anonymous: true }),
};

/**
 * Download a bearer-authed binary stream (e.g. a generated PDF) and save it
 * with the given filename. Throws ApiError on a non-2xx response (web).
 *
 * Mobile vs. web differ because two browser-download assumptions break inside a
 * Capacitor WebView:
 *   • the `<a download>` anchor is ignored by WKWebView / Android WebView, and
 *   • with CapacitorHttp enabled, the patched `fetch().blob()` can corrupt
 *     non-text (binary) bodies, yielding an empty / unreadable PDF.
 * So on device we let native code download straight to disk and share the file;
 * on the web we keep the authed fetch + download anchor.
 */
export async function downloadAuthedFile(path: string, filename: string): Promise<void> {
  const access = tokens.access;
  const headers = access ? { Authorization: `Bearer ${access}` } : undefined;

  if (Capacitor.isNativePlatform()) {
    await downloadRemoteFileNative(apiUrl(path), filename, headers);
    return;
  }

  const res = await fetch(apiUrl(path), { method: "GET", credentials: "include", headers });
  if (!res.ok) throw new ApiError(res.status, "DOWNLOAD_FAILED", `HTTP ${res.status}`);
  await saveBlob(await res.blob(), filename);
}
